import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { it, hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

function TicketList({ isAdmin = true }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [updateModal, setUpdateModal] = useState(null);
  const [updateForm, setUpdateForm] = useState({ status: '', assigned_to_id: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      const [tr, em] = await Promise.allSettled([
        isAdmin ? it.getTickets(params) : it.getMyTickets(),
        isAdmin ? hrms.getEmployees({ limit: 200 }) : Promise.resolve({ data: [] }),
      ]);
      if (tr.status === 'fulfilled') { const d = tr.value.data; setTickets(Array.isArray(d) ? d : d.items || []); }
      if (em.status === 'fulfilled') { const d = em.value.data; setEmployees(Array.isArray(d) ? d : d.items || []); }
    } catch { } finally { setLoading(false); }
  }, [filterStatus, filterPriority, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (updateForm.status) payload.status = updateForm.status;
      if (updateForm.assigned_to_id) payload.assigned_to_id = updateForm.assigned_to_id;
      if (updateForm.notes) payload.resolution_notes = updateForm.notes;
      await it.updateTicket(updateModal.id, payload);
      setUpdateModal(null); load();
    } catch { alert('Failed to update ticket.'); } finally { setSaving(false); }
  };

  const handleAcknowledge = async (id) => {
    if (!window.confirm('Confirm the issue is resolved and close this ticket?')) return;
    try { await it.acknowledgeTicket(id); load(); } catch { alert('Failed.'); }
  };

  const handleReopen = async (id) => {
    if (!window.confirm('Reopen this ticket? IT will be notified that the issue persists.')) return;
    try { await it.reopenTicket(id); load(); } catch { alert('Failed.'); }
  };

  const columns = [
    {
      header: 'Ticket', key: 'ticket_code',
      render: (v, row) => (
        <div>
          <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0, color: theme.info }}>{v || `#${row.id}`}</p>
          <p style={{ fontSize: '0.8rem', color: theme.text, margin: '0.15rem 0 0' }}>{row.title}</p>
        </div>
      ),
    },
    { header: 'Category', key: 'category', render: (v) => v || '—' },
    { header: 'Priority', key: 'priority', render: (v) => <Badge status={v || 'medium'} /> },
    { header: 'Status', key: 'status', render: (v) => <Badge status={v || 'open'} /> },
    { header: 'Raised By', key: 'raised_by_name', render: (v) => v || '—' },
    { header: 'Assigned To', key: 'assignee_name', render: (v) => v || <span style={{ color: theme.textMuted, fontSize: '0.8rem' }}>Unassigned</span> },
    {
      header: 'Age', key: 'created_at',
      render: (v) => {
        if (!v) return '—';
        const hours = (Date.now() - new Date(v).getTime()) / 3600000;
        const color = hours < 4 ? '#10B981' : hours < 24 ? '#F59E0B' : hours < 72 ? '#F97316' : '#EF4444';
        const label = hours < 1 ? `${Math.round(hours * 60)}m` : hours < 24 ? `${Math.round(hours)}h` : `${Math.round(hours / 24)}d`;
        return <span style={{ fontSize: '0.78rem', fontWeight: 700, color }}>{label}</span>;
      },
    },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button size="sm" variant="outline" onClick={() => navigate(`/it/tickets/${row.id}`)}>View</Button>
          {isAdmin && (
            <Button size="sm" variant="info" onClick={() => { setUpdateModal(row); setUpdateForm({ status: row.status || '', assigned_to_id: row.assigned_to?.id || '', notes: '' }); }}>Update</Button>
          )}
          {!isAdmin && row.status === 'resolved' && (
            <>
              <Button size="sm" variant="success" onClick={() => handleAcknowledge(row.id)}>✓ Acknowledge</Button>
              <Button size="sm" variant="danger" onClick={() => handleReopen(row.id)}>↺ Reopen</Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>
            {isAdmin ? 'All Tickets' : 'My Tickets'}
          </h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{tickets.length} tickets</p>
        </div>
        <Button variant="primary" onClick={() => navigate(isAdmin ? '/it/tickets/new' : '/employee/tickets/new')}>+ New Ticket</Button>
      </div>

      <Card padding="1rem">
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="escalated">Escalated</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
            style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
            <option value="">All Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <Button variant="outlineGray" size="sm" onClick={() => { setFilterStatus(''); setFilterPriority(''); }}>Clear</Button>
        </div>
      </Card>

      <Card padding="0">
        <Table columns={columns} data={tickets} loading={loading} emptyMessage="No tickets found" />
      </Card>

      <Modal isOpen={!!updateModal} onClose={() => setUpdateModal(null)} title={`Update Ticket: ${updateModal?.title}`}
        footer={<><Button variant="outlineGray" onClick={() => setUpdateModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleUpdate}>Update</Button></>}>
        <label style={lbl}>Status</label>
        <select style={inp} value={updateForm.status} onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <label style={lbl}>Assign To</label>
        <select style={inp} value={updateForm.assigned_to_id} onChange={(e) => setUpdateForm({ ...updateForm, assigned_to_id: e.target.value })}>
          <option value="">Select assignee</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
        </select>
        <label style={lbl}>Resolution Notes</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={updateForm.notes} onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })} />
      </Modal>
    </div>
  );
}

export default TicketList;
